import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { LoadingState, PageHeader, PageShell, StatCard } from "@/components/app/page";
import { adminAPI } from "@/services/api";
import { getErrorMessage, safeRender } from "@/utils/errorHelpers";
import { formatDate } from "@/utils/constants";
import { AlertTriangle, CheckCircle, Database, RefreshCw, Search, ServerCog, Wallet } from "lucide-react";
import { toast } from "sonner";

export default function SystemOpsPage() {
  const [loading, setLoading] = useState(true);
  const [health, setHealth] = useState(null);
  const [failed, setFailed] = useState([]);
  const [audit, setAudit] = useState(null);
  const [monitoring, setMonitoring] = useState(null);
  const [bonusClosures, setBonusClosures] = useState([]);
  const [closeMonth, setCloseMonth] = useState(getPreviousMonthKey());
  const [closingMonth, setClosingMonth] = useState(false);
  const [auditSearch, setAuditSearch] = useState("");
  const [retrying, setRetrying] = useState("");

  const load = async () => {
    try {
      const [healthRes, failedRes, auditRes, closuresRes] = await Promise.all([
        adminAPI.getSystemHealth(),
        adminAPI.listFailedOperations(),
        adminAPI.getFinancialAudit({ limit: 25 }),
        adminAPI.listBonusMonthClosures(),
      ]);
      setHealth(healthRes.data);
      setMonitoring(healthRes.data?.monitoring || null);
      setFailed(Array.isArray(failedRes.data) ? failedRes.data : []);
      setAudit(auditRes.data);
      setBonusClosures(Array.isArray(closuresRes.data) ? closuresRes.data : []);
    } catch (error) {
      toast.error(getErrorMessage(error, "Chargement système impossible"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const retry = async (orderId) => {
    setRetrying(orderId);
    try {
      await adminAPI.retryFailedOperation(orderId);
      toast.success("Operation relancee");
      await load();
    } catch (error) {
      toast.error(getErrorMessage(error, "Relance impossible"));
    } finally {
      setRetrying("");
    }
  };

  const searchAudit = async (event) => {
    event.preventDefault();
    try {
      const res = await adminAPI.getFinancialAudit({ source_transaction_id: auditSearch.trim() || undefined, limit: 50 });
      setAudit(res.data);
    } catch (error) {
      toast.error(getErrorMessage(error, "Audit impossible"));
    }
  };

  const closeBonuses = async (event) => {
    event.preventDefault();
    setClosingMonth(true);
    try {
      await adminAPI.closeBonusMonth(closeMonth);
      toast.success(`Mois ${closeMonth} archive et paiements bonus programmes`);
      await load();
    } catch (error) {
      toast.error(getErrorMessage(error, "Cloture impossible"));
    } finally {
      setClosingMonth(false);
    }
  };

  if (loading) return <LoadingState />;

  const checks = health?.checks || {};

  return (
    <PageShell data-testid="system-ops-page">
      <PageHeader
        eyebrow="Exploitation"
        title="Sante système et audit financier"
        description="Surveille les services critiques, relance les operations financieres echouees et reconstitue la trace d'une transaction."
        actions={
          <Button variant="outline" onClick={load} className="gap-2">
            <RefreshCw size={16} /> Actualiser
          </Button>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard title="Statut global" value={health?.status || "-"} icon={ServerCog} tone={health?.status === "ok" ? "green" : "red"} />
        <StatCard title="Operations a reprendre" value={health?.failed_operations || 0} icon={AlertTriangle} tone={health?.failed_operations ? "red" : "green"} />
        <StatCard title="Stockage" value={health?.storage_backend || "-"} icon={Database} tone="blue" />
        <StatCard title="Transactions auditees" value={audit?.summary?.transaction_count || 0} icon={Wallet} tone="gold" />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-[#2A4B46]">Monitoring leger</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-4">
          <div className="rounded-xl border border-[#E6E4E0] p-4">
            <p className="text-xs font-semibold uppercase text-[#8A8F8C]">Uptime</p>
            <p className="mt-2 text-xl font-bold text-[#2A4B46]">{formatDuration(monitoring?.uptime_seconds || 0)}</p>
          </div>
          <div className="rounded-xl border border-[#E6E4E0] p-4">
            <p className="text-xs font-semibold uppercase text-[#8A8F8C]">Requetes</p>
            <p className="mt-2 text-xl font-bold text-[#2A4B46]">{monitoring?.total_requests || 0}</p>
          </div>
          <div className="rounded-xl border border-[#E6E4E0] p-4">
            <p className="text-xs font-semibold uppercase text-[#8A8F8C]">Erreurs</p>
            <p className="mt-2 text-xl font-bold text-[#2A4B46]">{monitoring?.total_errors || 0}</p>
          </div>
          <div className="rounded-xl border border-[#E6E4E0] p-4">
            <p className="text-xs font-semibold uppercase text-[#8A8F8C]">Latence moyenne</p>
            <p className="mt-2 text-xl font-bold text-[#2A4B46]">{monitoring?.average_latency_ms || 0} ms</p>
          </div>
          <div className="md:col-span-4 rounded-xl bg-[#F8F5EF] p-4">
            <p className="font-semibold text-[#2A4B46]">Derniers incidents serveur</p>
            {(monitoring?.last_errors || []).length === 0 ? (
              <p className="mt-2 text-sm text-[#5C625F]">Aucun incident serveur observe depuis le dernier demarrage.</p>
            ) : (
              <div className="mt-3 space-y-2">
                {(monitoring?.last_errors || []).slice(0, 5).map((item, index) => (
                  <div key={`${item.created_at}-${index}`} className="rounded-lg bg-white p-3 text-sm">
                    <p className="font-semibold text-[#2A4B46]">{item.method} {item.path}</p>
                    <p className="text-[#5C625F]">Code {item.status_code} - {item.latency_ms || "-"} ms</p>
                    {item.error && <p className="break-words text-xs text-red-700">{item.error}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-[#2A4B46]">Services critiques</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-2">
          {Object.entries(checks).map(([key, check]) => (
            <div key={key} className="rounded-xl border border-[#E6E4E0] p-4">
              <div className="flex items-center justify-between gap-3">
                <p className="font-semibold text-[#2A4B46]">{check.label}</p>
                <span className={check.status === "ok" ? "text-green-700" : check.status === "warning" ? "text-amber-700" : "text-red-700"}>
                  {check.status === "ok" ? <CheckCircle size={18} /> : <AlertTriangle size={18} />}
                </span>
              </div>
              {check.détail && <p className="mt-2 break-words text-xs text-[#5C625F]">{check.détail}</p>}
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-[#2A4B46]">Cloture mensuelle, archive et paiements bonus</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <form onSubmit={closeBonuses} className="flex flex-col gap-3 sm:flex-row">
            <Input
              type="month"
              value={closeMonth}
              onChange={(event) => setCloseMonth(event.target.value)}
              max={getPreviousMonthKey()}
            />
            <Button type="submit" disabled={closingMonth} className="gap-2">
              <CheckCircle size={16} /> Cloturer et archiver
            </Button>
          </form>
          <p className="text-sm text-[#5C625F]">
            La cloture archive les transactions du mois, les partenaires actifs PAE/PAR au moins un jour, les bonus percus et la caisse restante. Elle programme aussi les paiements bonus mensuels pour traitement administratif.
          </p>
          <div className="grid gap-3 md:grid-cols-3">
            {bonusClosures.slice(0, 6).map((closure) => (
              <div key={closure.month} className="rounded-xl border border-[#E6E4E0] p-4 text-sm">
                <p className="font-semibold text-[#2A4B46]">{closure.month}</p>
                <p className="text-[#5C625F]">{closure.bonus_count || 0} bonus - {closure.total_bonus_mc || 0} MC</p>
                <p className="text-[#5C625F]">PAE: {closure.active_pae_count || 0} - PAR: {closure.active_par_count || 0}</p>
                <p className="text-[#5C625F]">Caisse restante: {closure.remaining_cash_mc || 0} MC</p>
                <p className="text-xs text-[#8A8F8C]">{closure.closed_at ? formatDate(closure.closed_at) : ""}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-[#2A4B46]">Operations a reprendre</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {failed.length === 0 ? (
            <p className="text-sm text-[#5C625F]">Aucune operation echouee.</p>
          ) : failed.map((order) => (
            <div key={order.id} className="flex flex-col gap-3 rounded-xl border border-[#E6E4E0] p-4 md:flex-row md:items-center md:justify-between">
              <div className="min-w-0">
                <p className="font-semibold text-[#2A4B46]">{order.id} - {order.buyer?.full_name || order.buyer_id}</p>
                <p className="text-sm text-[#5C625F]">{order.price_mc} MC - {formatDate(order.processing_failed_at || order.updated_at)}</p>
              </div>
              <Button onClick={() => retry(order.id)} disabled={retrying === order.id} className="gap-2">
                <RefreshCw size={16} /> Relancer
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-[#2A4B46]">Audit financier lisible</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <form onSubmit={searchAudit} className="flex flex-col gap-3 sm:flex-row">
            <Input value={auditSearch} onChange={(event) => setAuditSearch(event.target.value)} placeholder="ID commande ou transaction" />
            <Button type="submit" className="gap-2"><Search size={16} /> Rechercher</Button>
          </form>

          <div className="grid gap-3 lg:grid-cols-3">
            {(audit?.transactions || []).map((txn) => (
              <div key={txn.id} className="rounded-xl border border-[#E6E4E0] p-4">
                <p className="font-semibold text-[#2A4B46]">{txn.id}</p>
                <p className="mt-1 text-sm text-[#5C625F]">{txn.type} - {txn.amount_mc} MC</p>
                <p className="mt-1 text-xs text-[#8A8F8C]">{txn.source_transaction_id || txn.order_id}</p>
              </div>
            ))}
          </div>

          <div className="rounded-xl bg-[#F8F5EF] p-4 text-sm text-[#5C625F]">
            <p className="font-semibold text-[#2A4B46]">Resume</p>
            <p>Bonus lies: {audit?.summary?.bonus_count || 0}</p>
            <p>Total audite: {audit?.summary?.total_mc || 0} MC</p>
          </div>

          {(audit?.audit_logs || []).slice(0, 8).map((log) => (
            <div key={log.id} className="rounded-xl border border-[#E6E4E0] p-3 text-sm">
              <p className="font-semibold text-[#2A4B46]">{log.action}</p>
              <p className="text-[#5C625F]">{formatDate(log.created_at)} - {log.admin_name || log.executor_name || log.admin_id}</p>
              <p className="mt-1 break-words text-xs text-[#8A8F8C]">{safeRender(log.détails)}</p>
            </div>
          ))}
        </CardContent>
      </Card>
    </PageShell>
  );
}

function formatDuration(seconds) {
  const safe = Math.max(0, Number(seconds) || 0);
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function getPreviousMonthKey() {
  const date = new Date();
  date.setMonth(date.getMonth() - 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}
